import cloneDeep from 'lodash/cloneDeep';
import { stringifyForDataLayer } from '../../analytics/google/helpers/index.js';
import { transformProductConfig, hasPrice } from './productConfigTransformer';
export default function inventoryTransformer(inventory, options, inheritPageData, includeWalletData) {
  if (inventory === void 0) {
    inventory = {};
  }
  if (inheritPageData === void 0) {
    inheritPageData = true;
  }
  if (includeWalletData === void 0) {
    includeWalletData = false;
  }
  const inventoryCopy = cloneDeep(inventory);
  const {
    specifications,
    mileage,
    owner,
    accelerate,
    creditApp,
    isWestlakePrequal
  } = inventoryCopy;
  const {
    target,
    vehicleListingPosition,
    dataLayer = {},
    eventResult,
    onlyEcomDims = undefined
  } = options;
  const inventorySellerType = owner?.privateSeller ? 'private-seller' : 'dealer';
  const miles = mileage || specifications?.mileage;
  const {
    productConfiguration
  } = transformProductConfig({
    homeServices: {
      ...owner?.homeServices,
      accelerate
    },
    shippingLabel: owner?.shippingLabel,
    nds: hasPrice(inventoryCopy),
    westlake: isWestlakePrequal,
    creditApp: creditApp || accelerate
  }, includeWalletData);
  let translation = {};
  let bodyStyleCode;
  if (Array.isArray(inventoryCopy.bodyStyleCodes)) {
    bodyStyleCode = inventoryCopy.bodyStyleCodes[0];
  } else {
    // Using nullish coalescing(??) to validate only `null` and `undefined` value
    bodyStyleCode = inventoryCopy.bodyStyleCodes ?? inventoryCopy?.bodyStyles?.[0]?.code;
  }
  const style = Array.isArray(inventoryCopy.style) ? inventoryCopy.style[0] : inventoryCopy.style;
  const inventoryDetails = {
    bodyStyle: bodyStyleCode || style,
    chromeStyleId: inventoryCopy.chromeStyleId || inventoryCopy.styleId,
    condition: inventoryCopy.listingType || inventoryCopy.type,
    dealershipId: inventoryCopy.owner?.id?.toString() || inventoryCopy.ownerId?.toString(),
    dealerDistance: inventoryCopy?.owner?.distanceFromSearch,
    description: inventoryCopy.description?.label || inventoryCopy.additionalInfo?.vehicleDescription ? 'yes' : 'no',
    driveTrain: inventoryCopy.driveGroup || inventoryCopy.driveType?.name,
    engineType: inventoryCopy.engine?.name || inventoryCopy.engine,
    exteriorColor: inventoryCopy.exteriorColorSimple || inventoryCopy.specifications?.exterior?.value || inventoryCopy.color?.exteriorColorSimple,
    fuelEconomy: specifications?.mpg?.value,
    fuelType: inventoryCopy.fuelType?.name || inventoryCopy.fuelType,
    listingId: inventoryCopy.id || inventoryCopy.listingId,
    makeId: inventoryCopy.make?.code || inventoryCopy.makeCode,
    makeName: inventoryCopy.make?.name || inventoryCopy.makeName || inventoryCopy.make,
    mileage: miles?.value?.replace(/[^0-9]/g, '') || miles?.toString().replace(/[^0-9]/g, ''),
    modelId: inventoryCopy.model?.code || inventoryCopy.modelCode,
    modelName: inventoryCopy.model?.name || inventoryCopy.modelName || inventoryCopy.model,
    price: inventoryCopy.pricingDetail?.salePrice || inventoryCopy.pricingDetail?.msrp,
    priorityType: typeof inventoryCopy.priority === 'object' ? null : inventoryCopy.priority,
    productConfiguration,
    sellerType: inventorySellerType,
    series: inventoryCopy.classSeries,
    seriesId: inventoryCopy.classSeriesCode || inventoryCopy.series,
    transmission: inventoryCopy.transmissionGroup || inventoryCopy.transmission?.name,
    trimId: inventoryCopy.trim?.code || inventoryCopy.atTrim || inventoryCopy.trimCode,
    trimName: inventoryCopy.trim?.name || inventoryCopy.trim,
    vehicleId: inventoryCopy.id || inventoryCopy.listingId,
    videoId: inventoryCopy.videoId,
    vin: inventoryCopy.vin,
    year: inventoryCopy.year
  };
  switch (target) {
    case 'ecommerce':
      {
        const ecommerceDimensions = {
          // Vehicle Catalog
          dimension1: dataLayer.catalog,
          // Vehicle ID
          dimension3: inventoryCopy.id,
          // Vehicle Listing ID
          dimension4: inventoryCopy.id,
          // Vehicle Year
          dimension5: inventoryCopy.year,
          // Vehicle Make ID
          dimension6: inventoryCopy.make?.code || inventoryCopy.makeCode,
          // Vehicle Model ID
          dimension8: inventoryCopy.model?.code || inventoryCopy.modelCode,
          // Vehicle Market Model
          dimension10: inventoryCopy.model?.name || inventoryCopy.model,
          // Vehicle Series ID
          dimension11: inventoryCopy.classSeriesCode,
          // Vehicle Trim ID
          dimension12: inventoryCopy.trim?.code || inventoryCopy.atTrim || inventoryCopy.trimCode,
          // Vehicle Trim Name
          dimension13: inventoryCopy.trim?.name || inventoryCopy.trim,
          // Vehicle Type
          dimension14: inventoryCopy.type,
          // Vehicle Body Style
          dimension15: Array.isArray(inventoryCopy.style) ? inventoryCopy.style[0] : inventoryCopy.style,
          // Vehicle Condition
          dimension16: inventoryCopy.listingType || inventoryCopy.type,
          // Vehicle Mileage
          dimension17: miles?.value?.replace(/[^0-9]/g, '') || miles?.toString().replace(/[\s,]+/g, ''),
          // Vehicle Fuel
          dimension18: inventoryCopy.fuelType?.name || inventoryCopy.fuelType,
          // Vehicle Fuel Econony
          dimension19: specifications?.mpg?.value,
          // Vehicle Engine Type
          dimension20: specifications?.engine?.value,
          // Vehicle Drivetrain
          dimension21: specifications?.driveType?.value,
          // Vehicle Price
          dimension22: inventoryCopy.pricingDetail?.salePrice || inventoryCopy.pricingDetail?.msrp,
          // Vehicle Seller Type
          dimension23: inventorySellerType,
          // Vehicle Product Configuration
          dimension24: productConfiguration,
          // Vehicle Listing Position
          dimension25: vehicleListingPosition,
          // Event Result
          dimension30: eventResult,
          // Dealer Priority Type
          dimension31: inventoryCopy.priority,
          // Dealership ID
          dimension33: owner ? owner.id : inventoryCopy.ownerId,
          // Dealer Distance
          dimension34: inventoryCopy?.owner?.distanceFromSearch
        };
        if (onlyEcomDims !== undefined) {
          Object.keys(ecommerceDimensions).forEach(key => {
            if (!onlyEcomDims.includes(key)) delete ecommerceDimensions[key];
          });
        }
        translation = stringifyForDataLayer(ecommerceDimensions);
        break;
      }
    case 'pageData':
      {
        translation = stringifyForDataLayer({
          ...inventoryDetails
        });
        break;
      }
    case 'vehicle':
      {
        translation = stringifyForDataLayer({
          ...inventoryDetails,
          catalog: dataLayer.catalog,
          clickType: inventoryCopy.clickType,
          subCategory: dataLayer.consumerInsights?.pe_subcat,
          vehicleType: inventoryCopy.type
        }, inheritPageData);
        break;
      }
    case 'search':
      {
        translation = stringifyForDataLayer({
          catalog: dataLayer.catalog,
          yearMin: inventoryCopy.startYear || 'any',
          yearMax: inventoryCopy.endYear || 'any',
          makeName: inventoryCopy.makeCodeList,
          modelName: inventoryCopy.modelCodeList,
          seriesName: inventoryCopy.seriesCodeList,
          trimName: inventoryCopy.trimCodeList,
          bodyStyle: inventoryCopy.vehicleStyleCodes,
          vehicleCondition: inventoryCopy.listingTypes,
          mileage: inventoryCopy.maxMileage,
          // TODO: How should the price range look?
          price: inventoryCopy.minPrice && inventoryCopy.maxPrice ? `${inventoryCopy.minPrice}-${inventoryCopy.maxPrice}` : undefined,
          vehicleHistory: inventoryCopy.vhrTypes,
          sellerType: inventoryCopy.sellerTypes,
          productType: inventoryCopy.homeServices,
          radius: inventoryCopy.searchRadius,
          zipCode: inventoryCopy.zip,
          keywords: inventoryCopy.keywordPhrases
        });
        break;
      }
    case 'video':
      {
        translation = stringifyForDataLayer({
          ...inventoryDetails,
          clickType: inventoryCopy.clickType,
          catalog: dataLayer.catalog || undefined,
          year: inventoryCopy.year || undefined,
          makeId: inventoryCopy.make?.code || inventoryCopy.makeCode || undefined,
          makeName: inventoryCopy.make?.name || inventoryCopy.make,
          modelId: inventoryCopy.mode?.code || inventoryCopy.modelCode || undefined,
          modelName: inventoryCopy.model?.name || inventoryCopy.model
        });
        break;
      }
    default:
  }
  return translation;
}